/**
 * PATH: packages/email-hub/src/feature/company-context/CompanyContextDrawer.tsx
 * FILE_ID: EMAILHUB-COMPANYCONTEXT-DRAWER-001
 * PURPOSE: Email-domain CRM proxy using the shared SplitDrawer frame.
 * WHY: A company label is context, not a destructive filter. The drawer lets
 *      triage continue while exposing CRM identity, people, work, activity,
 *      and a deliberate path into the full CRM surface.
 * FLOW: CompanyChip -> drawer query -> bounded tenant-fenced payload -> peek,
 *       correct/filter/open-CRM actions.
 * RELATED: CompanyChip.tsx, backend /api/kanban/company-context/
 */
import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Building2,
  ExternalLink,
  FileText,
  Mail,
  Pencil,
  RefreshCw,
  Users,
  BriefcaseBusiness,
} from 'lucide-react'
import { SplitDrawer } from '@quotech/ui'

import { emailApi } from '../lib/api'
import { useToast } from '../../_internal/components/Toast'
import { useApiAuth } from '../../_internal/components/ApiAuthGate'
import {
  companyContextKey,
  fetchCompanyContext,
} from './companyContextApi'
import { useCompanyScopeStore } from '../stores/companyScopeStore'

export interface CompanyContextDrawerProps {
  open: boolean
  domain: string
  label: string
  onClose: () => void
  /** Explicit filter action; opening the drawer never invokes this. */
  onShowEmails?: () => void
  onChanged?: () => void
}

function useCompanyContext(
  tenantId: number | null,
  domain: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: companyContextKey(tenantId, domain),
    enabled: enabled && tenantId != null && Boolean(domain),
    staleTime: 60_000,
    queryFn: () => fetchCompanyContext(domain),
  })
}

export function CompanyContextDrawer({
  open,
  domain,
  label,
  onClose,
  onShowEmails,
  onChanged,
}: CompanyContextDrawerProps) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { session } = useApiAuth()
  const tenantId = session?.organisation?.id ?? null
  const context = useCompanyContext(tenantId, domain, open)
  const [pinned, setPinned] = useState(false)
  const [width, setWidth] = useState(480)
  const [editing, setEditing] = useState(false)
  const [companyName, setCompanyName] = useState(label)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setEditing(false)
      setCompanyName(context.data?.company.client_name || label)
    }
  }, [open, context.data?.company.client_name, label])

  const company = context.data?.company
  const openCrm = () => {
    if (!company?.crm_path) return
    onClose()
    navigate(company.crm_path)
  }
  const saveCompany = async () => {
    const clientName = companyName.trim()
    if (!clientName) return
    setSaving(true)
    try {
      await emailApi('/api/kanban/senders/company/', {
        method: 'POST',
        body: JSON.stringify({ domain, client_name: clientName }),
        headers: { 'Content-Type': 'application/json' },
      })
      // P23 — keep the active company filter pill in sync with the rename.
      useCompanyScopeStore.getState().setCompanyLabel(clientName)
      await context.refetch()
      setEditing(false)
      onChanged?.()
      toast('Company context updated', 'success')
    } catch {
      toast('Could not update the company. Try again.', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <SplitDrawer
      isOpen={open}
      onClose={onClose}
      isPinned={pinned}
      onPinnedChange={setPinned}
      width={width}
      onWidthChange={setWidth}
      minWidth={360}
      maxWidth={640}
      title={company?.client_name || label}
      subtitle={domain}
      icon={Building2}
      mobileTitle="Company context"
      headerActions={
        company?.crm_path ? (
          <button
            type="button"
            onClick={openCrm}
            className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-[11px] text-primary hover:bg-primary/10"
            title="Open full CRM profile"
          >
            CRM <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : undefined
      }
    >
      <div className="flex h-full flex-col" data-testid="company-context-drawer">
      <div className="flex-1 overflow-y-auto p-4">
        {(context.isLoading || (open && tenantId == null)) && <DrawerLoading />}
        {context.isError && (
          <DrawerState
            label="Company context could not load."
            action={<RetryButton onClick={() => void context.refetch()} />}
          />
        )}
        {context.data && (
          <div className="space-y-4">
            <section className="rounded-lg border border-border bg-muted/20 p-3">
              <SectionHeading icon={Building2} label="Overview" />
              {editing ? (
                <div className="mt-2 flex gap-2">
                  <input
                    value={companyName}
                    onChange={(event) => setCompanyName(event.target.value)}
                    aria-label="Company name"
                    className="min-w-0 flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                  />
                  <button
                    type="button"
                    disabled={saving || !companyName.trim()}
                    onClick={() => void saveCompany()}
                    className="rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => {
                      setCompanyName(company?.client_name || label)
                      setEditing(false)
                    }}
                    className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="mt-1 flex items-center gap-2">
                  <p className="min-w-0 flex-1 text-sm font-medium">
                    {context.data.company.client_name || context.data.company.suggested_label}
                  </p>
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    <Pencil className="h-3 w-3" aria-hidden /> Correct
                  </button>
                </div>
              )}
              <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                {context.data.identity.roles.slice(0, 3).map((role) => (
                  <span key={role.role} className="rounded-full border border-border px-2 py-0.5">
                    {role.role_label || role.role}
                  </span>
                ))}
                {context.data.identity.category_hint && (
                  <span className="rounded-full border border-border px-2 py-0.5 text-muted-foreground">
                    {context.data.identity.category_hint}
                  </span>
                )}
              </div>
            </section>

            <section>
              <SectionHeading icon={Users} label={`People (${context.data.people.length})`} />
              {context.data.people.length ? (
                <div className="mt-2 space-y-2">
                  {context.data.people.map((person) => (
                    <div key={person.person.id} className="rounded-md border border-border/70 p-2">
                      <p className="text-sm font-medium">{person.person.display_name || person.person.primary_email}</p>
                      <p className="truncate text-[11px] text-muted-foreground">{person.person.primary_email}</p>
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        {person.emails.count} emails · {person.documents.count} documents
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyCopy>No resolved people at this domain yet.</EmptyCopy>
              )}
            </section>

            <section>
              <SectionHeading icon={BriefcaseBusiness} label="Work" />
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Metric label="Jobs" value={String(context.data.work.jobs.length)} />
                <Metric label="Documents" value={String(context.data.work.document_count)} />
              </div>
              {context.data.work.jobs.slice(0, 4).map((job) => (
                <div key={job.job_id} className="mt-2 flex items-center gap-2 text-xs">
                  <BriefcaseBusiness className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{job.label}</span>
                  <span className="shrink-0 text-muted-foreground">{job.email_count}</span>
                </div>
              ))}
            </section>

            <section>
              <SectionHeading icon={Mail} label={`Activity (${context.data.activity.visible_email_count})`} />
              {context.data.activity.recent_emails.length ? (
                <div className="mt-2 space-y-2">
                  {context.data.activity.recent_emails.map((email) => (
                    <div key={email.id} className="rounded-md border border-border/70 p-2">
                      <p className="truncate text-xs font-medium">{email.subject}</p>
                      <p className="mt-0.5 text-[10px] text-muted-foreground">
                        {new Date(email.received_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyCopy>No visible email activity for this company.</EmptyCopy>
              )}
            </section>

            <section className="rounded-lg border border-border/70 p-3">
              <SectionHeading icon={FileText} label="Context" />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Identity and work context is derived from the active organisation and only mail you can already see.
              </p>
              <p className="mt-1 text-[10px] text-muted-foreground/80">
                Source: {context.data.identity.provenance}
              </p>
            </section>
          </div>
        )}
        </div>
        {onShowEmails && (
          <div className="border-t border-border p-3">
            <button
              type="button"
              onClick={() => {
                onClose()
                onShowEmails()
              }}
              className="flex w-full items-center justify-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-accent"
            >
              <Mail className="h-4 w-4" aria-hidden /> Show emails from this company
            </button>
          </div>
        )}
      </div>
    </SplitDrawer>
  )
}

function SectionHeading({
  icon: Icon,
  label,
}: {
  icon: typeof Building2
  label: string
}) {
  return (
    <h3 className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      <Icon className="h-3.5 w-3.5" aria-hidden /> {label}
    </h3>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-muted/50 p-2">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-base font-semibold">{value}</p>
    </div>
  )
}

function EmptyCopy({ children }: { children: ReactNode }) {
  return <p className="mt-2 text-xs text-muted-foreground">{children}</p>
}

function DrawerLoading() {
  return (
    <div className="space-y-3" role="status" aria-label="Loading company context">
      {[1, 2, 3].map((item) => (
        <div key={item} className="h-20 animate-pulse rounded-lg bg-muted/60" />
      ))}
    </div>
  )
}

function DrawerState({
  label,
  action,
}: {
  label: string
  action?: ReactNode
}) {
  return (
    <div className="flex min-h-48 flex-col items-center justify-center gap-3 text-center">
      <p className="text-sm text-muted-foreground">{label}</p>
      {action}
    </div>
  )
}

function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs hover:bg-accent"
    >
      <RefreshCw className="h-3.5 w-3.5" aria-hidden /> Retry
    </button>
  )
}

export default CompanyContextDrawer
